import { useState, type FormEvent } from "react";
import { Aviso, Botao, Campo, CampoDeTelefone, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { CampoDeCep } from "./CampoDeCep";
import { useEdicaoDoEndereco } from "./useEdicaoDoEndereco";
import {
  cepLegivel,
  useContatos,
  useEnderecos,
  useRemoverContato,
  useRemoverEndereco,
  useSalvarContato,
  useSalvarEndereco,
  type ContatoDaOrganizacao,
  type EnderecoDaOrganizacao,
} from "../../nucleo/cadastro";

/* ---------------------------------------------------------------------------------- contatos */

const CONTATO_EM_BRANCO = { nome: "", cargo: "", email: "", telefone: "", principal: false };

export function ContatosDaOrganizacao({
  organizacaoId,
  podeEditar,
}: {
  organizacaoId: string;
  podeEditar: boolean;
}) {
  const contatos = useContatos(organizacaoId);
  const salvar = useSalvarContato(organizacaoId);
  const remover = useRemoverContato(organizacaoId);
  const [editando, setEditando] = useState<ContatoDaOrganizacao | "novo" | null>(null);
  const [form, setForm] = useState(CONTATO_EM_BRANCO);

  function abrir(contato: ContatoDaOrganizacao | "novo") {
    setEditando(contato);
    setForm(
      contato === "novo"
        ? CONTATO_EM_BRANCO
        : {
            nome: contato.nome,
            cargo: contato.cargo ?? "",
            email: contato.email ?? "",
            telefone: contato.telefone ?? "",
            principal: contato.principal,
          },
    );
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!form.nome.trim()) return;
    salvar.mutate(
      {
        id: editando && editando !== "novo" ? editando.id : undefined,
        corpo: {
          nome: form.nome.trim(),
          cargo: form.cargo.trim() || null,
          email: form.email.trim() || null,
          telefone: form.telefone.trim() || null,
          principal: form.principal,
        },
      },
      { onSuccess: () => setEditando(null) },
    );
  }

  return (
    <section aria-labelledby="titulo-contatos" data-testid="contatos-da-organizacao">
      <h2 className="titulo-secao" id="titulo-contatos">
        Contatos
      </h2>
      {contatos.isPending ? <p role="status">Carregando contatos…</p> : null}
      {contatos.isError ? <Aviso tom="erro">{mensagemDoConsole(contatos.error)}</Aviso> : null}
      {contatos.data && contatos.data.length === 0 ? (
        <Aviso tom="marca" className="mb-3">Nenhum contato anotado.</Aviso>
      ) : null}
      <div className="lista-de-itens mb-3">
        {contatos.data?.map((contato) => (
          <Cartao
            key={contato.id}
            titulo={contato.nome}
            data-testid={`contato-${contato.nome}`}
            acessorio={contato.principal ? <Chip estado="ok">Principal</Chip> : null}
          >
            <p>
              {[contato.cargo, contato.email, contato.telefone].filter(Boolean).join(" · ") || "—"}
            </p>
            {podeEditar ? (
              <div className="flex gap-2 mt-2">
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  aria-label={`Editar contato ${contato.nome}`}
                  onClick={() => abrir(contato)}
                >
                  Editar
                </Botao>
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  aria-label={`Remover contato ${contato.nome}`}
                  disabled={remover.isPending}
                  onClick={() => remover.mutate(contato.id)}
                >
                  Remover
                </Botao>
              </div>
            ) : null}
          </Cartao>
        ))}
      </div>
      {remover.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(remover.error)}</Aviso> : null}
      {podeEditar && editando === null ? (
        <Botao variante="fantasma" onClick={() => abrir("novo")} data-testid="novo-contato">
          Adicionar contato
        </Botao>
      ) : null}
      {editando !== null ? (
        <form onSubmit={enviar} noValidate className="cartao" data-testid="formulario-de-contato">
          <div className="grid gap-3 md:grid-cols-2">
            <Campo
              rotulo="Nome"
              obrigatorio
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
            />
            <Campo rotulo="Cargo" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} />
            <Campo
              rotulo="E-mail"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <CampoDeTelefone
              rotulo="Telefone"
              valor={form.telefone}
              aoMudar={(telefone) => setForm({ ...form, telefone })}
            />
          </div>
          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={form.principal}
              onChange={(e) => setForm({ ...form, principal: e.target.checked })}
            />
            <span>Contato principal</span>
          </label>
          {salvar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(salvar.error)}</Aviso> : null}
          <div className="flex gap-2">
            <Botao type="submit" disabled={salvar.isPending || !form.nome.trim()}>
              {salvar.isPending ? "Salvando…" : "Salvar contato"}
            </Botao>
            <Botao variante="fantasma" onClick={() => setEditando(null)}>
              Cancelar
            </Botao>
          </div>
        </form>
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------------- endereços */

const ENDERECO_EM_BRANCO = {
  rotulo: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
  principal: false,
};

export function EnderecosDaOrganizacao({
  organizacaoId,
  podeEditar,
}: {
  organizacaoId: string;
  podeEditar: boolean;
}) {
  const enderecos = useEnderecos(organizacaoId);
  const salvar = useSalvarEndereco(organizacaoId);
  const remover = useRemoverEndereco(organizacaoId);
  const [editando, setEditando] = useState<EnderecoDaOrganizacao | "novo" | null>(null);
  const [form, setForm] = useState(ENDERECO_EM_BRANCO);
  const edicao = useEdicaoDoEndereco();

  function abrir(endereco: EnderecoDaOrganizacao | "novo") {
    setEditando(endereco);
    setForm(
      endereco === "novo"
        ? ENDERECO_EM_BRANCO
        : {
            rotulo: endereco.rotulo,
            cep: cepLegivel(endereco.cep),
            logradouro: endereco.logradouro ?? "",
            numero: endereco.numero ?? "",
            complemento: endereco.complemento ?? "",
            bairro: endereco.bairro ?? "",
            cidade: endereco.cidade ?? "",
            uf: endereco.uf ?? "",
            principal: endereco.principal,
          },
    );
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!form.rotulo.trim()) return;
    const texto = (valor: string) => valor.trim() || null;
    salvar.mutate(
      {
        id: editando && editando !== "novo" ? editando.id : undefined,
        corpo: {
          rotulo: form.rotulo.trim(),
          cep: texto(form.cep),
          logradouro: texto(form.logradouro),
          numero: texto(form.numero),
          complemento: texto(form.complemento),
          bairro: texto(form.bairro),
          cidade: texto(form.cidade),
          uf: texto(form.uf),
          principal: form.principal,
        },
      },
      { onSuccess: () => setEditando(null) },
    );
  }

  return (
    <section aria-labelledby="titulo-enderecos" data-testid="enderecos-da-organizacao">
      <h2 className="titulo-secao" id="titulo-enderecos">
        Endereços
      </h2>
      {enderecos.isPending ? <p role="status">Carregando endereços…</p> : null}
      {enderecos.isError ? <Aviso tom="erro">{mensagemDoConsole(enderecos.error)}</Aviso> : null}
      {enderecos.data && enderecos.data.length === 0 ? (
        <Aviso tom="marca" className="mb-3">Nenhum endereço anotado.</Aviso>
      ) : null}
      <div className="lista-de-itens mb-3">
        {enderecos.data?.map((endereco) => (
          <Cartao
            key={endereco.id}
            titulo={endereco.rotulo}
            data-testid={`endereco-${endereco.rotulo}`}
            acessorio={endereco.principal ? <Chip estado="ok">Principal</Chip> : null}
          >
            <p>
              {[
                [endereco.logradouro, endereco.numero].filter(Boolean).join(", "),
                endereco.complemento,
                endereco.bairro,
                [endereco.cidade, endereco.uf].filter(Boolean).join(" / "),
                cepLegivel(endereco.cep),
              ]
                .filter(Boolean)
                .join(" · ") || "—"}
            </p>
            {podeEditar ? (
              <div className="flex gap-2 mt-2">
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  aria-label={`Editar endereço ${endereco.rotulo}`}
                  onClick={() => abrir(endereco)}
                >
                  Editar
                </Botao>
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  aria-label={`Remover endereço ${endereco.rotulo}`}
                  disabled={remover.isPending}
                  onClick={() => remover.mutate(endereco.id)}
                >
                  Remover
                </Botao>
              </div>
            ) : null}
          </Cartao>
        ))}
      </div>
      {remover.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(remover.error)}</Aviso> : null}
      {podeEditar && editando === null ? (
        <Botao variante="fantasma" onClick={() => abrir("novo")} data-testid="novo-endereco">
          Adicionar endereço
        </Botao>
      ) : null}
      {editando !== null ? (
        <form onSubmit={enviar} noValidate className="cartao" data-testid="formulario-de-endereco">
          <div className="grid gap-3 md:grid-cols-3">
            <Campo
              rotulo="Rótulo (ex.: Matriz)"
              obrigatorio
              value={form.rotulo}
              onChange={(e) => setForm({ ...form, rotulo: e.target.value })}
            />
            <CampoDeCep
              value={form.cep}
              onChange={(cep) => setForm((atual) => ({ ...atual, cep }))}
              aoIniciarBusca={edicao.iniciar}
              onEndereco={(achado) => setForm((atual) => edicao.mesclar(atual, achado))}
            />
            <Campo
              rotulo="Logradouro"
              value={form.logradouro}
              onChange={(e) => {
                edicao.marcar("logradouro");
                setForm({ ...form, logradouro: e.target.value });
              }}
            />
            <Campo rotulo="Número" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} />
            <Campo
              rotulo="Complemento"
              value={form.complemento}
              onChange={(e) => setForm({ ...form, complemento: e.target.value })}
            />
            <Campo rotulo="Bairro" value={form.bairro} onChange={(e) => {
                edicao.marcar("bairro");
                setForm({ ...form, bairro: e.target.value });
              }} />
            <Campo rotulo="Cidade" value={form.cidade} onChange={(e) => {
                edicao.marcar("cidade");
                setForm({ ...form, cidade: e.target.value });
              }} />
            <Campo
              rotulo="UF"
              maxLength={2}
              value={form.uf}
              onChange={(e) => {
                edicao.marcar("uf");
                setForm({ ...form, uf: e.target.value });
              }}
            />
          </div>
          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={form.principal}
              onChange={(e) => setForm({ ...form, principal: e.target.checked })}
            />
            <span>Endereço principal</span>
          </label>
          {salvar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(salvar.error)}</Aviso> : null}
          <div className="flex gap-2">
            <Botao type="submit" disabled={salvar.isPending || !form.rotulo.trim()}>
              {salvar.isPending ? "Salvando…" : "Salvar endereço"}
            </Botao>
            <Botao variante="fantasma" onClick={() => setEditando(null)}>
              Cancelar
            </Botao>
          </div>
        </form>
      ) : null}
    </section>
  );
}
